/**
 * Lobby System Types for Local Links
 * 
 * A Lobby is a waiting room for golf games/events. Players join lobbies
 * to find playing partners, similar to video game matchmaking.
 * 
 * Lobby Hierarchy:
 * - Personal: Created by individual users (max 8 players)
 * - League/Club Event: Created by league/club admins (max 100 players)
 */

// ============================================================================
// LOBBY TYPES
// ============================================================================

/**
 * Type of lobby - determines capacity and visibility options
 */
export type LobbyType = 
  | 'personal'        // Temporary individual lobby
  | 'league_event'    // Tied to a league
  | 'club_event'      // Tied to a club
  | 'persistent_group'; // A recurring/persistent lobby (e.g. weekly skins group)

/**
 * Who can see and join this lobby
 */
export type LobbyVisibility = 
  | 'public'       // Anyone can see and join
  | 'private'      // Only invited users can join
  | 'members_only' // Only league/club members can join (for league_event/club_event)
  | 'invite_code'; // Requires a code to join

/**
 * Current state of the lobby
 */
export type LobbyStatus = 
  | 'open'         // Accepting new players
  | 'full'         // At capacity, accepting waitlist
  | 'starting'     // Lobby locked, game about to start
  | 'in_progress'  // Game is currently happening
  | 'completed'    // Game finished
  | 'cancelled';   // Lobby was cancelled

/**
 * Type of golf activity
 */
export type GameType = 
  | 'casual'       // Relaxed round, no stakes
  | 'competitive'  // Keeping score, maybe friendly wager
  | 'tournament'   // Structured tournament round
  | 'practice'     // Range session or practice round
  | 'lesson';      // Teaching/learning session

// ============================================================================
// CAPACITY LIMITS
// ============================================================================

export const LOBBY_CAPACITY = {
  personal: {
    min: 2,
    max: 8,
  },
  league_event: {
    min: 2,
    max: 100,
  },
  club_event: {
    min: 2,
    max: 100, // Same as league per user requirement
  },
  persistent_group: {
    min: 2,
    max: 100, // Persistent groups (weekly cart, skins game) can be large
  },
} as const;

// ============================================================================
// CORE INTERFACES
// ============================================================================

/**
 * A player/participant in a lobby
 */
export interface LobbyPlayer {
  id: string;
  userId: string;
  displayName: string;
  photoURL?: string;
  joinedAt: Date;
  status: 'confirmed' | 'pending' | 'declined';
  role: 'host' | 'member' | 'waitlist';
}

/**
 * Lobby invite code for private/invite_code lobbies
 */
export interface LobbyInvite {
  code: string;
  lobbyId: string;
  createdBy: string;
  createdAt: Date;
  expiresAt?: Date;
  maxUses?: number;
  uses: number;
}

/**
 * Main Lobby entity
 */
export interface Lobby {
  id: string;
  type: LobbyType;
  visibility: LobbyVisibility;
  status: LobbyStatus;
  
  // Creator
  createdBy: string;
  createdByName: string;
  
  // Context (which entity owns this lobby, if any)
  leagueId?: string;
  leagueName?: string;
  clubId?: string;
  clubName?: string;
  courseId?: string;
  courseName?: string;
  
  // Game/Event details
  title: string;
  description?: string;
  gameType: GameType;
  
  // Scheduling
  scheduledDate?: Date;        // When the game is scheduled
  scheduledTime?: string;      // e.g., "10:00 AM"
  isFlexibleTime?: boolean;    // Can start when enough players join
  
  // Format & Identity (New Architectural Requirements)
  isPersistent: boolean;      // Temporary round vs permanent squad
  captainId?: string;         // The squad leader (distinct from creator)
  size: number;               // Format size (1=Solo, 2=Cart, 4=Scramble)
  
  // Capacity Limits
  minPlayers: number;
  maxPlayers: number;
  
  // Players
  players: LobbyPlayer[];
  waitlist: LobbyPlayer[];
  
  // Optional invite code (for invite_code visibility)
  inviteCode?: string;
  
  // Metadata
  createdAt: Date;
  updatedAt: Date;
  expiresAt?: Date;           // Auto-cancel if not started
  startedAt?: Date;           // When game began
  completedAt?: Date;         // When game ended
  
  // Match result ID (if converted to a match)
  matchId?: string;
}

/**
 * Filters for browsing lobbies
 */
export interface LobbyFilters {
  type?: LobbyType | LobbyType[];
  gameType?: GameType | GameType[];
  visibility?: LobbyVisibility;
  status?: LobbyStatus | LobbyStatus[];
  leagueId?: string;
  clubId?: string;
  courseId?: string;
  hasOpenSpots?: boolean;
  scheduledAfter?: Date;
  scheduledBefore?: Date;
  nearLocation?: {
    lat: number;
    lng: number;
    radiusMiles: number;
  };
}

/**
 * Input for creating a new lobby
 */
export interface CreateLobbyInput {
  type: LobbyType;
  visibility: LobbyVisibility;
  title: string;
  description?: string;
  gameType: GameType;
  
  // Context (required depending on type)
  leagueId?: string;  // Required for league_event
  clubId?: string;    // Required for club_event
  courseId?: string;  // Optional - where game will be played
  
  // Scheduling
  scheduledDate?: Date;
  scheduledTime?: string;
  isFlexibleTime?: boolean;
  
  // Format & Identity
  isPersistent?: boolean;
  captainId?: string;
  size?: number;
  
  // Capacity (within type limits)
  minPlayers?: number;
  maxPlayers?: number;
  
  // Auto-expire
  expiresInHours?: number;
}

/**
 * Result of joining a lobby
 */
export interface JoinLobbyResult {
  success: boolean;
  lobby: Lobby;
  playerStatus: 'confirmed' | 'waitlist';
  message?: string;
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get capacity limits for a lobby type
 */
export function getLobbyCapacity(type: LobbyType) {
  return LOBBY_CAPACITY[type];
}

/**
 * Check if a lobby has open spots
 */
export function hasOpenSpots(lobby: Lobby): boolean {
  const confirmedPlayers = lobby.players.filter(p => p.status === 'confirmed');
  return confirmedPlayers.length < lobby.maxPlayers;
}

/**
 * Check if lobby meets minimum player requirement
 */
export function canStartLobby(lobby: Lobby): boolean {
  const confirmedPlayers = lobby.players.filter(p => p.status === 'confirmed');
  return confirmedPlayers.length >= lobby.minPlayers;
}

/**
 * Get the number of open spots in a lobby
 */
export function getOpenSpots(lobby: Lobby): number {
  const confirmedPlayers = lobby.players.filter(p => p.status === 'confirmed');
  return Math.max(0, lobby.maxPlayers - confirmedPlayers.length);
}

/**
 * Check if a user is in a lobby (as player or waitlist)
 */
export function isUserInLobby(lobby: Lobby, userId: string): boolean {
  return (
    lobby.players.some(p => p.userId === userId) ||
    lobby.waitlist.some(p => p.userId === userId)
  );
}

/**
 * Get user's role in a lobby
 */
export function getUserLobbyRole(lobby: Lobby, userId: string): LobbyPlayer['role'] | 'waitlist' | null {
  const player = lobby.players.find(p => p.userId === userId);
  if (player) return player.role;
  
  if (lobby.waitlist?.some(w => w.userId === userId)) return 'waitlist';
  
  return null;
}

// ============================================================================
// LOBBY CHAT TYPES
// ============================================================================

export interface LobbyMessage {
  id: string;
  lobbyId: string;
  senderId: string;
  senderName: string;
  senderImage?: string;
  content: string;
  type: "text" | "system";
  createdAt: string; // ISO string
}

export interface SendLobbyMessageInput {
  content: string;
}
